import { useTranslation } from "react-i18next";
import { PublicCheckoutDetails, PublicCheckoutProduct, PublicCheckoutProductPrice } from "../data/checkout";
import { isColorLight, getContrastColor } from "../utils/colors";
import { formatCurrency } from "../utils/format";
import Header from "./header";

interface CheckoutSuccessProps {
  checkoutData: PublicCheckoutDetails;
  paidAmount?: number; // Actual amount paid (may be discounted)
  selectedCurrency?: string;
}

function getProductPrice(
  product: PublicCheckoutProduct,
  currency: string,
): PublicCheckoutProductPrice {
  if (product.prices?.length) {
    const match = product.prices.find(
      (p) => p.currency.toLowerCase() === currency.toLowerCase(),
    );
    if (match) return match;
    return product.prices[0];
  }
  return product.price;
}

function CheckoutSuccess({ checkoutData, paidAmount, selectedCurrency }: CheckoutSuccessProps) {
  const { t, i18n } = useTranslation();

  const brandColor = checkoutData.business.brandColor || "#eb5e27";
  const secondaryColor = checkoutData.business.secondaryColor || "#FFF7F0";
  const businessName = checkoutData.business.name || "";

  const isLightBg = isColorLight(secondaryColor);
  const textColor = getContrastColor(secondaryColor);
  const textMutedColor = isLightBg
    ? "rgba(0,0,0,0.6)"
    : "rgba(255,255,255,0.7)";

  const mainProduct = checkoutData.products[0];
  const resolvedCurrency = selectedCurrency || checkoutData.defaultCurrency || mainProduct.price.currency;
  const mainPrice = getProductPrice(mainProduct, resolvedCurrency);
  const originalAmount = checkoutData.products.reduce(
    (sum, p) => sum + getProductPrice(p, resolvedCurrency).unitAmount,
    0,
  );
  // Use paid amount if provided (for discounted payments), otherwise use original
  const displayAmount = paidAmount ?? originalAmount;
  const currency = mainPrice.currency;

  return (
    <div className="min-h-screen" style={{ backgroundColor: secondaryColor }}>
      {checkoutData.business.icon && (
        <link rel="icon" href={checkoutData.business.icon} />
      )}
      <Header
        icon={checkoutData.business.icon}
        businessName={businessName}
        textColor={textColor}
      />

      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl">
          {/* Success icon */}
          <div className="mb-6 text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: `${brandColor}15` }}
            >
              <svg
                className="h-8 w-8"
                style={{ color: brandColor }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {checkoutData.mode === "subscription"
                ? t("gymkeePay.checkout.successSubscription", {
                    defaultValue: "Subscription active!",
                  })
                : t("gymkeePay.checkout.successPayment", {
                    defaultValue: "Payment successful!",
                  })}
            </h2>
            <p className="mt-2 text-gray-600">
              {t("gymkeePay.paymentSuccess.thankYou")}
            </p>
          </div>

          {/* Amount */}
          <div className="mb-6 rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-sm text-gray-500">
              {t("gymkeePay.paymentSuccess.amountPaid")}
            </p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(displayAmount, currency)}
            </p>
            {checkoutData.mode === "subscription" &&
              mainPrice.recurring && (
                <p className="mt-1 text-sm text-gray-500">
                  /{" "}
                  {mainPrice.recurring.intervalCount > 1
                    ? `${mainPrice.recurring.intervalCount} `
                    : ""}
                  {t(
                    `gymkeePay.frequency.${mainPrice.recurring.interval === "month" ? "monthly" : mainPrice.recurring.interval === "year" ? "yearly" : mainPrice.recurring.interval === "week" ? "weekly" : "daily"}`,
                  ).toLowerCase()}
                </p>
              )}
          </div>

          {/* Product details */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {t("gymkeePay.checkout.product", { defaultValue: "Product" })}
              </span>
              <span className="font-medium text-gray-900">
                {mainProduct.name}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {t("gymkeePay.paymentSuccess.date")}
              </span>
              <span className="font-medium text-gray-900">
                {new Date().toLocaleDateString(i18n.language, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>

          {/* Subscription confirmation */}
          {checkoutData.mode === "subscription" && (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <p className="text-sm text-green-700">
                  {t("gymkeePay.paymentSuccess.subscriptionActivated", {
                    defaultValue: "Your subscription is now active",
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Delivery Information - Service/Appointment */}
          {mainProduct.deliveryMethod === "service" && mainProduct.serviceConfig && (
            <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-blue-800">
                    {t("gymkeePay.paymentSuccess.serviceTitle", {
                      defaultValue: "Book your appointment",
                    })}
                  </p>
                  <p className="mt-1 text-sm text-blue-700">
                    {mainProduct.serviceConfig.type === "visio" &&
                      t("gymkeePay.paymentSuccess.serviceTypeVisio", {
                        defaultValue: "Video call",
                      })}
                    {mainProduct.serviceConfig.type === "in_person" &&
                      t("gymkeePay.paymentSuccess.serviceTypeInPerson", {
                        defaultValue: "In-person meeting",
                      })}
                    {mainProduct.serviceConfig.type === "mixed" &&
                      t("gymkeePay.paymentSuccess.serviceTypeMixed", {
                        defaultValue: "Video call or in-person",
                      })}
                    {" · "}
                    {mainProduct.serviceConfig.duration}{" "}
                    {t("gymkeePay.paymentSuccess.minutes", {
                      defaultValue: "min",
                    })}
                  </p>
                  {mainProduct.serviceConfig.calendarLink && (
                    <a
                      href={mainProduct.serviceConfig.calendarLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: brandColor }}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {t("gymkeePay.paymentSuccess.bookNow", {
                        defaultValue: "Book now",
                      })}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Post-payment message (for external or service delivery) */}
          {mainProduct.postPaymentMessage &&
            (mainProduct.deliveryMethod === "external" ||
              mainProduct.deliveryMethod === "service") && (
              <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">
                      {t("gymkeePay.paymentSuccess.importantInfo", {
                        defaultValue: "Important information",
                      })}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-amber-700">
                      {mainProduct.postPaymentMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Gymkee delivery - Download app CTA */}
          {(!mainProduct.deliveryMethod ||
            mainProduct.deliveryMethod === "gymkee") && (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-green-800">
                    {t("gymkeePay.paymentSuccess.accessContent", {
                      defaultValue: "Access your content",
                    })}
                  </p>
                  <p className="mt-1 text-sm text-green-700">
                    {t("gymkeePay.paymentSuccess.downloadApp", {
                      defaultValue:
                        "Download Gymkee on your phone to access your content.",
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact info */}
          {checkoutData.business.supportEmail && (
            <div className="mt-6 border-t border-gray-200 pt-4 text-center">
              <p className="text-sm text-gray-500">
                {t("gymkeePay.paymentSuccess.questionsAboutPayment")}{" "}
                <a
                  href={`mailto:${checkoutData.business.supportEmail}`}
                  className="font-medium hover:underline"
                  style={{ color: brandColor }}
                >
                  {t("gymkeePay.paymentSuccess.contactBusiness", {
                    businessName,
                  })}
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: textMutedColor }}>
            {t("gymkeePay.footer.poweredBy")}{" "}
            <a
              href="https://gymkee.io"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: textColor }}
            >
              Gymkee
            </a>
            , {t("gymkeePay.footer.securedBy")}{" "}
            <span className="font-medium" style={{ color: textColor }}>
              Stripe
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccess;
